import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import './App.css';
import './i18n';

import HomePage from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import ResetPassword from './pages/ResetPassword';
import Onboarding from './pages/Onboarding';
import AppShell from './components/AppShell';
import Feed from './pages/Feed';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import Shop from './pages/Shop';
import Dashboard from './pages/Dashboard';
import CommunityList from './pages/CommunityList';
import CommunityPage from './pages/CommunityPage';
import SearchPage from './pages/SearchPage';
import Discover from './pages/Discover';
import Messages from './pages/Messages';
// Brief Jul 6 2026 P0 §3 — Booking (Type→Date→Heure flow) supprimé.
// Ancienne route /booking/:expertId → redirige vers /app/profile/:expertId.
import AIAssistant from './pages/AIAssistant';
import MySessions from './pages/MySessions';
import Subscription from './pages/Subscription';
import Settings from './pages/Settings';
import Contact from './pages/Contact';
import AppHome from './pages/AppHome';
import StatusPage from './pages/StatusPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import AdminWeeklyPicks from './pages/AdminWeeklyPicks';
// Brief B4 §1 + §2 — Beta launch admin dashboard + MaIA diagnostic
import AdminDashboard from './pages/AdminDashboard';
import AdminMaiaDiagnostic from './pages/AdminMaiaDiagnostic';
import CGU from './pages/legal/CGU';
import Privacy from './pages/legal/Privacy';
import Mentions from './pages/legal/Mentions';
// Brief Vague 2 §7 — Public event share page
import PublicEventPage from './pages/PublicEventPage';
import PublicPostPage from './pages/PublicPostPage';
import PublicProductPage from './pages/PublicProductPage';
// Brief Prospection Phase 1 — liens publics universels experts
import PublicExpertPage from './pages/PublicExpertPage';
import PublicEventSlugPage from './pages/PublicEventSlugPage';
import GuestConfirmedPage from './pages/GuestConfirmedPage';
import FeedbackPage from './pages/FeedbackPage';
// Brief Jul 10 2026 — ProspectionDashboard déplacé dans Mes sessions (?tab=prospection)
// Brief Jun 17 §1 — Global render error boundary (prevents white-page crashes)
import ErrorBoundary from './components/ErrorBoundary';

// Sprint 1.0c V1.a-bis (Bug 5) — preserve communityId on /communities/:id → /community/:id
const PluralCommunityRedirect = () => {
  const { pathname } = useLocation();
  const target = pathname.replace('/app/communities', '/app/community');
  return <Navigate to={target} replace />;
};

// Brief Prospection Phase 1 — les routes /@handle sont capturées par /:handle ;
// on ne rend la page publique que si le segment commence bien par « @ ».
const HandleGate = ({ children }) => {
  const { pathname } = useLocation();
  const first = pathname.split('/').filter(Boolean)[0] || '';
  if (!first.startsWith('@')) return <Navigate to="/" replace />;
  return children;
};

// Brief Jul 6 2026 P0 §3 — Ancien flow /booking/:expertId (Type→Date→Heure)
// remplacé par le profil public + timeline events + calendrier disponibilité.
const BookingRedirect = () => {
  const { pathname } = useLocation();
  const expertId = pathname.split('/').pop();
  // Brief LOT1 — l'ancien flow /booking/:expertId ne meurt plus en silence :
  // ?book=1 ouvre la modal de réservation sur le profil (flux unifié).
  return <Navigate to={`/app/profile/${expertId}?book=1`} replace />;
};

// Brief B4 §2 — convenience alias for typing /admin/maia/:userId directly
const AdminMaiaAlias = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/admin/, '/app/admin')} replace />;
};

const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1f35]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not completed (unless we're already going there)
  if (requireOnboarding && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1f35]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If onboarding already completed, go to feed
  if (user.onboarding_completed) {
    return <Navigate to="/app/home" replace />;
  }

  return children;
};

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public legal pages (no auth required) */}
      <Route path="/legal/cgu" element={<CGU />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/mentions" element={<Mentions />} />

      {/* Brief Vague 2 §7 — Page événement publique partageable (no auth) */}
      <Route path="/event/:eventId" element={<PublicEventPage />} />
      {/* Brief LOT3 — pages publiques dédiées pour le partage (sans compte) */}
      <Route path="/post/:postId" element={<PublicPostPage />} />
      <Route path="/product/:productId" element={<PublicProductPage />} />
      {/* Brief Prospection Phase 1 — pages publiques universelles (React Router
          v7 n'autorise pas les segments partiels /@:handle — on capture
          /:handle et on vérifie le préfixe @ dans HandleGate). */}
      <Route path="/booking/confirmed" element={<GuestConfirmedPage />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/:handle" element={<HandleGate><PublicExpertPage /></HandleGate>} />
      <Route path="/:handle/e/:slug" element={<HandleGate><PublicEventSlugPage /></HandleGate>} />
      
      {/* Onboarding Route */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <Onboarding />
          </OnboardingRoute>
        }
      />
      
      {/* App Routes with Shell */}
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="home" element={<AppHome />} />
        <Route path="feed" element={<Feed />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile/:userId" element={<Profile />} />
        <Route path="shop/:userId" element={<Shop />} />
        {/* Sprint 1.0c Bug M — Dashboard fusionné dans Accueil. Le composant
            Dashboard reste accessible via redirect au cas où des liens externes
            l'utilisent encore. */}
        <Route path="dashboard" element={<Navigate to="/app/home" replace />} />
        <Route path="dashboard-legacy" element={<Dashboard />} />
        <Route path="messages" element={<Messages />} />
        <Route path="messages/:conversationId" element={<Messages />} />
        <Route path="community" element={<CommunityList />} />
        <Route path="community/:communityId" element={<CommunityPage />} />
        {/* Sprint 1.0c V1.a-bis (Bug 5) — defensive redirects for plural variants */}
        <Route path="communities" element={<Navigate to="/app/community" replace />} />
        <Route path="communities/:communityId" element={<PluralCommunityRedirect />} />
        <Route path="search" element={<SearchPage />} />
        {/* Brief Jul 6 2026 P0 §3 — Ancien flow multi-étapes "Type de session"
            supprimé. Redirection vers le profil public de l'expert qui expose
            désormais la timeline events + le calendrier disponibilité. */}
        <Route path="booking/:expertId" element={<BookingRedirect />} />
        <Route path="assistant" element={<AIAssistant />} />
        <Route path="discover" element={<Discover />} />
        <Route path="sessions" element={<MySessions />} />
        <Route path="prospection" element={<Navigate to="/app/sessions?tab=prospection" replace />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="settings" element={<Settings />} />
        <Route path="contact" element={<Contact />} />
        <Route path="payment/success" element={<PaymentSuccess />} />
        <Route path="payment/cancel" element={<PaymentCancel />} />
        <Route path="admin/weekly-picks" element={<AdminWeeklyPicks />} />
        {/* Brief B4 §1 + §2 — beta launch dashboard + MaIA diagnostic */}
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="admin/maia/:userId" element={<AdminMaiaDiagnostic />} />
        <Route index element={<Navigate to="/app/home" replace />} />
      </Route>

      {/* Convenience aliases — typing /admin/... without /app/ prefix should still work */}
      <Route path="/admin/weekly-picks" element={<Navigate to="/app/admin/weekly-picks" replace />} />
      <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
      <Route path="/admin/maia/:userId" element={<AdminMaiaAlias />} />
      <Route path="/admin/*" element={<Navigate to="/app/home" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <HelmetProvider>
          <ThemeProvider>
            <BrowserRouter>
              <AuthProvider>
                <AppRouter />
                <Toaster position="bottom-right" richColors closeButton />
              </AuthProvider>
            </BrowserRouter>
          </ThemeProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
